
import React, { useCallback, useMemo } from 'react';
import { Material } from '~/types';
import classes from './style.module.scss';
import { useStoreActions, useStoreState } from '~/store';
import Button from '~/app/common/Button';
import Quantity from '../../Quantity';
import Icon from '~/app/common/Icon';
import useTranslation from '~/app/hooks/useTranslation';
import InfiniteScroll from 'react-infinite-scroll-component';
import { MaterialTypes } from '~/app/data/statuses/issue';
import useAccessControl from '~/app/hooks/useAccessControl';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { toast } from 'react-toastify';
import { IssueOrServiceType } from '~/app/data/statuses/chat';

export interface SelectedMaterialListProps {
  searchText: string
  editManualMaterial: (material: Material) => void
  type: IssueOrServiceType
}

export type SelectedMaterialListComponent = React.FC<SelectedMaterialListProps>

const SelectedMaterialList: SelectedMaterialListComponent = ({ searchText, editManualMaterial, type }) => {

  const basePath = (type === IssueOrServiceType.ISSUE )? 'issue' : 'service';
  const deptPrefix = useStoreState(state => state[basePath].data.departmentPrefix);
  const tempMaterials = useStoreState(state => state[basePath].tempMaterials ?? []);
  const searchMaterials = useStoreState(state => state[basePath].searchMaterials ?? []);
  const searchMaterialsTotal = useStoreState(state => state[basePath].searchMaterialsTotal ?? 0);
  const searchMaterialAction = useStoreActions(actions => actions[basePath].fetchSearchMaterials ?? []);
  const setTempMaterialsAction = useStoreActions(actions => actions[basePath].setTempMaterials);

  const { t } = useTranslation('material');
  const { checkPermission } = useAccessControl();

  const materialOutOfStockPermission = deptPrefix 
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.materialOutOfStock(deptPrefix))
    : false;

  const displayedMaterials = useMemo(() => {
    if (searchText === '') {
      return tempMaterials;
    }

    const tempMap = new Map(tempMaterials.filter(tm => tm.workOrderMaterialId !== undefined).map(mat => [mat?.workOrderMaterialId?.toString().trim(), mat]));
    return searchMaterials.map(mat => tempMap.get(mat?.workOrderMaterialId?.toString().trim()) || mat);
  }, [searchText, tempMaterials, searchMaterials]);

  const getAllowUpdate = useCallback((material: Material, quantity: number) => {
    if (!materialOutOfStockPermission && material.type === MaterialTypes.AUTO) {
      const inStock = (material.in_stock - quantity) >= 0;
      if (!inStock) {
        toast.error(t('outOfStock'));
      }
      return inStock;
    }
    return true;
  }, [materialOutOfStockPermission, t]);

  const handleAddMaterial = useCallback((material: Material) => {
    const alreadyExists = tempMaterials.some(m => m.id.toString().trim() === material.id);
    if (!alreadyExists) {
      const updatedMaterials = [...tempMaterials]
      updatedMaterials.push({ ...material, quantity: 1, added: true })
      setTempMaterialsAction(updatedMaterials);
    }
  }, [tempMaterials, setTempMaterialsAction]);

  const handleQuantityChange = useCallback((id: string, value: number) => {
    const updatedMaterials = [...tempMaterials];
    const index = updatedMaterials.findIndex(tm => tm.id === id);

    if (index !== -1) {
      const existingMaterial = updatedMaterials[index];
      if (getAllowUpdate(existingMaterial, value)) {
        updatedMaterials[index] = { ...existingMaterial, quantity: value };
      }
    } else {
      const materialFromSearch = searchMaterials.find(m => m.id === id);
      if (materialFromSearch && getAllowUpdate(materialFromSearch, value)) {
        updatedMaterials.push({ ...materialFromSearch, quantity: value, added: true });
      }
    }

    setTempMaterialsAction(updatedMaterials);
  }, [tempMaterials, searchMaterials, setTempMaterialsAction, getAllowUpdate]);

  const handleDelete = (id: string) => {
    const updatedMaterials = [...tempMaterials];
    const index = updatedMaterials.findIndex(tm => tm.id === id);
    if (index !== -1) {
      updatedMaterials[index] = {
        ...updatedMaterials[index],
        deleted: true,
      };
      setTempMaterialsAction(updatedMaterials);
    }
  }

  const fetchNext = () => {
    searchMaterialAction({ searchText, offset: searchMaterials.length });
  }

  return (
    <div className={classes['partsList']} id='search-scroll-wrapper'>
      {displayedMaterials.length === 0 ? (
        <p>{t('noPartsFound')}</p>
      ) : (
        <InfiniteScroll
          dataLength={searchMaterials.length}
          next={fetchNext}
          hasMore={searchMaterials.length < searchMaterialsTotal}
          loader={<div></div>}
          scrollableTarget="search-scroll-wrapper"
        >
          {displayedMaterials.filter(dM => !dM.deleted).map((material, index) => (
            <div className={classes['partItem']} key={index}>
              <div className={classes['row']}>
                <div className={classes['column']}>
                  <button className={classes['deleteButton']} onClick={() => handleDelete(material.id)}>
                    <Icon name={'delete'} />
                  </button>
                </div>
                <div className={classes['column']}>
                  <span className={classes['partName']}>
                      {material.article_name} 
                      {material.type === MaterialTypes.MANUAL && <Icon name={'edit'} onClick={() => editManualMaterial(material)} />}
                    </span>
                  <span className={classes['partNumber']}>{t('partNo')}: {material.article_no}</span>
                </div>
              </div>
              <div className={classes['column']}>
                {material.added ? (
                    <Quantity quantity={material.quantity} quantityChange={(value) => handleQuantityChange(material.id, value)} />
                  ) : (
                    materialOutOfStockPermission === false && material.in_stock < 1 ? (
                      <p className={classes['outOfStock']}>{t('outOfStock')}</p>
                    ) : (
                      <Button className={classes['addButton']} onClick={() => handleAddMaterial(material)}>{t('add')}</Button>
                    )
                  )
                }
              </div>
            </div>
          ))}
        </InfiniteScroll>
      )}
    </div>
  );
};

export default SelectedMaterialList;